import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import C from '../colors';
import { getPicturePreviewUrl } from '../pictureUrls';

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12"/>
    </svg>
  );
}

function ChevronIcon({ direction }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {direction === 'previous' ? <path d="M15 18l-6-6 6-6"/> : <path d="M9 18l6-6-6-6"/>}
    </svg>
  );
}

function ReadonlyImagePreview({ pictures, initialPictureId, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadFailed, setLoadFailed] = useState(false);
  const closeButtonRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const isOpen = initialPictureId !== null && initialPictureId !== undefined && pictures.length > 0;

  useEffect(() => {
    if (!isOpen) return;
    const index = pictures.findIndex(picture => String(picture.id) === String(initialPictureId));
    setCurrentIndex(index >= 0 ? index : 0);
  }, [initialPictureId, isOpen, pictures]);

  useEffect(() => {
    setLoadFailed(false);
  }, [currentIndex, initialPictureId]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex(index => Math.max(0, index - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex(index => Math.min(pictures.length - 1, index + 1));
  }, [pictures.length]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    previouslyFocusedRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = event => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedRef.current?.focus?.();
    };
  }, [handleNext, handlePrevious, isOpen, onClose]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrevious,
    preventScrollOnSwipe: true,
    trackMouse: false,
  });

  if (!isOpen) return null;

  const picture = pictures[currentIndex];
  const filename = picture?.originalFileName || picture?.fileName || '未命名照片';
  const previewUrl = getPicturePreviewUrl(picture);

  return (
    <div
      {...swipeHandlers}
      role="dialog"
      aria-modal="true"
      aria-label={`照片预览：${filename}`}
      data-testid="readonly-image-preview"
      style={{
        position: 'fixed', inset: 0, zIndex: 1400,
        display: 'flex', flexDirection: 'column',
        background: 'rgba(10,8,9,.96)', color: '#fff',
        animation: 'readonlyPreviewFadeIn .18s ease-out',
      }}
    >
      <style>{`
        @keyframes readonlyPreviewFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @media (max-width: 720px) {
          .readonly-preview-arrow { width: 38px !important; height: 46px !important; }
          .readonly-preview-arrow--previous { left: 8px !important; }
          .readonly-preview-arrow--next { right: 8px !important; }
        }
      `}</style>

      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2,
        display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center',
        padding: '16px 20px 36px',
        background: 'linear-gradient(to bottom, rgba(0,0,0,.72), transparent)',
      }}>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="关闭照片预览"
          data-testid="readonly-preview-close"
          style={{
            justifySelf: 'start', width: 40, height: 40, borderRadius: 10,
            display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#fff',
            background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.18)',
          }}
        >
          <CloseIcon/>
        </button>
        <span data-testid="readonly-preview-counter" style={{
          fontFamily: C.mono, fontSize: 12, letterSpacing: 1,
          color: 'rgba(255,255,255,.72)', fontVariantNumeric: 'tabular-nums',
        }}>
          {currentIndex + 1} / {pictures.length}
        </span>
        <span style={{ justifySelf: 'end', fontFamily: C.sans, fontSize: 11, color: 'rgba(255,255,255,.46)' }}>
          只读预览
        </span>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', padding: '72px 64px 70px' }}>
        {!loadFailed && previewUrl ? (
          <img
            key={picture?.id}
            src={previewUrl}
            alt={filename}
            data-testid="readonly-preview-image"
            onError={() => setLoadFailed(true)}
            draggable="false"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', userSelect: 'none' }}
          />
        ) : (
          <div data-testid="readonly-preview-error" role="status" style={{
            width: 'min(480px, 80vw)', minHeight: 220, borderRadius: 12,
            display: 'grid', placeItems: 'center', textAlign: 'center', padding: 24,
            background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)',
            fontFamily: C.sans, fontSize: 13, lineHeight: 1.6, color: 'rgba(255,255,255,.65)',
          }}>
            图片加载失败，请关闭后重试
          </div>
        )}
      </div>

      {currentIndex > 0 && (
        <button
          type="button"
          onClick={handlePrevious}
          aria-label="上一张"
          data-testid="readonly-preview-previous"
          className="readonly-preview-arrow readonly-preview-arrow--previous"
          style={{
            position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)',
            width: 46, height: 54, borderRadius: 12, display: 'grid', placeItems: 'center',
            color: '#fff', cursor: 'pointer', background: 'rgba(255,255,255,.1)',
            border: '1px solid rgba(255,255,255,.18)', backdropFilter: 'blur(8px)',
          }}
        >
          <ChevronIcon direction="previous"/>
        </button>
      )}

      {currentIndex < pictures.length - 1 && (
        <button
          type="button"
          onClick={handleNext}
          aria-label="下一张"
          data-testid="readonly-preview-next"
          className="readonly-preview-arrow readonly-preview-arrow--next"
          style={{
            position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)',
            width: 46, height: 54, borderRadius: 12, display: 'grid', placeItems: 'center',
            color: '#fff', cursor: 'pointer', background: 'rgba(255,255,255,.1)',
            border: '1px solid rgba(255,255,255,.18)', backdropFilter: 'blur(8px)',
          }}
        >
          <ChevronIcon direction="next"/>
        </button>
      )}

      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        padding: '34px 24px 20px', textAlign: 'center', pointerEvents: 'none',
        background: 'linear-gradient(to top, rgba(0,0,0,.72), transparent)',
      }}>
        <span data-testid="readonly-preview-filename" style={{
          fontFamily: C.mono, fontSize: 11, letterSpacing: .4, color: 'rgba(255,255,255,.7)',
        }}>
          {filename}
        </span>
      </div>
    </div>
  );
}

export default ReadonlyImagePreview;
